<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { getTestStateStore } from '../../../../testsStateStore.js';
    import { __ } from '@oat-sa-private/ui-core';
    import { Button } from '@oat-sa-private/ui-elements';
    import Counter from './Counter.svelte';
    import OverviewButton from './OverviewButton.svelte';
    import TooltipNavButton from '../controls/TooltipNavButton.svelte';

    /**
     * Component displays test navigation in the special case where multiple attempts can be taken on an item
     */

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId - id of test session
     * @property {Object} navigationState - hash of which actions are allowed
     * @property {String} [navButtonSize]
     * @property {Boolean} [disabled] - is navigation disabled
     * @property {Boolean} [disableForward] - is forward navigation disabled
     * @property {Boolean} [focusableDisableNext] - keep forward navigation focusable while blocking it
     * @property {Boolean} [linearNavDelayActive] - block forward navigation while the linear navigation delay is running
     * @property {String} [guidedNavigationTooltipText]
     * @property {Function} onNext
     * @property {Function} onSkip
     * @property {Function} onAttempt
     * @property {Function} onSubmitpart
     * @property {Function} onOverview
     */
    /** @type {Props} */
    let {
        serviceCallId,
        navigationState,
        navButtonSize = 'small',
        disabled = false,
        disableForward = false,
        focusableDisableNext = false,
        linearNavDelayActive = false,
        guidedNavigationTooltipText = '',
        onNext,
        onSkip,
        onAttempt,
        onSubmitpart,
        onOverview
    } = $props();

    // svelte-ignore state_referenced_locally
    const testStateStore = getTestStateStore(serviceCallId);

    let part = $derived($testStateStore ? testStateStore.getCurrentTestPart() : null);
    let testContext = $derived($testStateStore ? testStateStore.getTestContext() : null);
    let stats = $derived(part?.stats);
    let currentPosition = $derived(part && testContext ? testContext.itemPosition - part.position : 0);

    let forwardActionVisuallyDisabled = $derived(!disabled && disableForward && focusableDisableNext);
    let forwardActionDisabled = $derived(disabled || (disableForward && !focusableDisableNext) || linearNavDelayActive);
    let tooltipProps = $derived(
        forwardActionVisuallyDisabled
            ? {
                  visuallyDisabled: true,
                  tooltipText: guidedNavigationTooltipText
              }
            : {}
    );
    let skipTooltipProps = $derived(forwardActionVisuallyDisabled ? { ...tooltipProps, wrapperOrder: -1 } : {});
</script>

<style>
    .button-container {
        display: flex;
        flex: 0 0 auto;

        &.start {
            flex-basis: 8rem;
            margin-block: 0;
            margin-inline: 0 2rem;
        }
        &.center :global(button) {
            margin: 0 1rem;
        }
        &.end {
            margin-block: 0;
            margin-inline: 2rem 0;
            display: flex;
            flex: 0 0 auto;

            & > :global(button[name='skip']) {
                margin-block: 0;
                margin-inline: 0 1rem;
                order: -1;
            }
            & > :global(.tooltip-nav-button) :global(button[name='skip']) {
                margin-block: 0;
                margin-inline: 0 1rem;
            }
        }
        & > :global(button),
        & > :global(.tooltip-nav-button) {
            display: inline-flex;
        }
        & > :global(button),
        & > :global(.tooltip-nav-button) :global(button) {
            margin: 0;
        }
    }

    /* Non-mobile */
    @media screen and (--mq-minwidth-medium) {
        .button-container.start {
            order: 1;
        }
        .button-container.center {
            order: 2;
        }
        .button-container.end {
            order: 3;
        }
    }

    /* Mobile */
    @media screen and (--mq-maxwidth-medium) {
        .button-container.start {
            order: 1;
            flex: 1 0 0;
        }
        .button-container.center {
            order: 2;
            margin: 0 var(--space-half);
        }
        /* order: 3 is for button[name='bookmark'] in TestNavigator */
        .button-container.end {
            order: 4;
            flex: 1 0 0;
            justify-content: flex-end;
        }
    }
</style>

{#if navigationState}
    {@const ButtonComponent = forwardActionVisuallyDisabled ? TooltipNavButton : Button}
    <div class="button-container end">
        {#if navigationState.isLast && navigationState.allowed.attemptsDone}
            {#if navigationState.allowed.finishTest}
                <ButtonComponent
                    name="finish"
                    label={__('Finish the test')}
                    shape="pill"
                    size={navButtonSize}
                    skin="primary"
                    icon="finish-16"
                    iconSide="right"
                    disabled={forwardActionDisabled}
                    {...tooltipProps}
                    on:click={onSubmitpart} />
            {:else if navigationState.allowed.finishTestPart}
                <ButtonComponent
                    name="submit"
                    label={__('Submit test part')}
                    shape="pill"
                    size={navButtonSize}
                    skin="primary"
                    icon="submit-16"
                    iconSide="right"
                    disabled={forwardActionDisabled}
                    {...tooltipProps}
                    on:click={onSubmitpart} />
            {/if}
        {/if}

        {#if navigationState.allowed.attempt}
            <ButtonComponent
                name="attempt"
                label={__('Attempt (%d left)', navigationState.remainingAttempts)}
                ariaLabel={__(
                    'Attempt. You have %d attempts left for this question.',
                    navigationState.remainingAttempts
                )}
                shape="pill"
                size={navButtonSize}
                skin="primary"
                disabled={forwardActionDisabled}
                {...tooltipProps}
                on:click={onAttempt} />
        {:else if navigationState.allowed.attemptsDone && !navigationState.isLast}
            <ButtonComponent
                name="next"
                label={__('Next question')}
                ariaLabel={__('Go to next question')}
                shape="pill"
                size={navButtonSize}
                skin="primary"
                icon="arrow-right-16"
                iconSide="right"
                iconRtlFlip={true}
                disabled={forwardActionDisabled}
                {...tooltipProps}
                on:click={() => onSkip?.({ direction: 'next' })} />
        {/if}

        {#if navigationState.isLinear && navigationState.allowed.skip && !navigationState.isLast}
            <ButtonComponent
                name="skip"
                label={__('Skip question')}
                shape="pill"
                size={navButtonSize}
                skin="secondary"
                disabled={forwardActionDisabled}
                {...skipTooltipProps}
                on:click={() => onSkip?.({ direction: 'next' })} />
        {/if}
    </div>

    <div class="button-container start">
        {#if navigationState.isLinear}
            <Counter total={stats.total} position={currentPosition + 1} />
        {/if}
    </div>

    <div class="button-container center">
        {#if !navigationState.isLinear}
            {#if navigationState.allowed.previous && navigationState.canNavigateFreely}
                <Button
                    name="prev"
                    ariaLabel={__('Go to previous question')}
                    shape="circular"
                    size="small"
                    skin="secondary"
                    icon="arrow-left-16"
                    iconRtlFlip={true}
                    on:click={() => onSkip?.({ direction: 'previous' })}
                    {disabled} />
            {/if}

            {#if navigationState.allowed.overview}
                <OverviewButton {serviceCallId} {disabled} {onOverview} />
            {/if}

            {#if navigationState.allowed.skip && !navigationState.isLast}
                {#if !navigationState.allowed.attemptsDone}
                    <Button
                        name="skip"
                        ariaLabel={__('Skip question')}
                        shape="circular"
                        size="small"
                        skin="secondary"
                        icon="arrow-right-16"
                        iconRtlFlip={true}
                        on:click={() => onSkip?.({ direction: 'next' })}
                        disabled={forwardActionDisabled} />
                {:else}
                    <ButtonComponent
                        name="next"
                        label={__('Next question')}
                        ariaLabel={__('Go to next question')}
                        shape="circular"
                        size="small"
                        skin="secondary"
                        icon="arrow-right-16"
                        iconRtlFlip={true}
                        {...tooltipProps}
                        on:click={onNext}
                        disabled={forwardActionDisabled} />
                {/if}
            {/if}
        {/if}
    </div>
{/if}
